import React, { useState, useEffect } from 'react';
import { Reservation, House } from '../../services/types';
import { getStatusColor } from '../../utils/reservationHelpers';
import moment from 'moment';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'react-bootstrap';
import { useUserData } from '../../hooks';
import './CalendarView.css';
import './MultiMonthCalendarView.css';

interface CalendarViewProps {
    reservations: Reservation[];
    houses: House[];
    onSelectReservation: (reservation: Reservation) => void;
    onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
    clearSelection?: boolean;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_VISIBLE_EVENTS = 15;

const CalendarView: React.FC<CalendarViewProps> = ({
    reservations,
    houses,
    onSelectReservation,
    onSelectSlot,
    clearSelection,
}) => {
    const { role } = useUserData();
    const isAdminOrSuperAdmin = role === 'Admin' || role === 'Super Admin';

    const [currentMonth, setCurrentMonth] = useState<{ year: number; month: number }>({
        year: moment().year(),
        month: moment().month(),
    });
    const [selectedStart, setSelectedStart] = useState<Date | null>(null);
    const [selectedEnd, setSelectedEnd] = useState<Date | null>(null);
    const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());
    const [hoveredReservationId, setHoveredReservationId] = useState<string | null>(null);

    // Clear selection when clearSelection prop changes
    useEffect(() => {
        if (clearSelection) {
            setSelectedStart(null);
            setSelectedEnd(null);
            setHoveredDate(null);
        }
    }, [clearSelection]);

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 920);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Handle ESC key to clear check-in selection
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && selectedStart && !selectedEnd) {
                setSelectedStart(null);
                setHoveredDate(null);
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [selectedStart, selectedEnd]);

    const handleClearSelection = () => {
        setSelectedStart(null);
        setSelectedEnd(null);
        setHoveredDate(null);
    };

    const handlePrevMonth = () => {
        setCurrentMonth((prev) => {
            const newDate = moment().year(prev.year).month(prev.month).subtract(1, 'month');
            return { year: newDate.year(), month: newDate.month() };
        });
    };

    const handleNextMonth = () => {
        setCurrentMonth((prev) => {
            const newDate = moment().year(prev.year).month(prev.month).add(1, 'month');
            return { year: newDate.year(), month: newDate.month() };
        });
    };

    const handleToday = () => {
        const today = moment();
        setCurrentMonth({ year: today.year(), month: today.month() });
    };

    const handleDateClick = (date: Date) => {
        if (!onSelectSlot) return;

        const isPast = moment(date).isBefore(moment(), 'day');
        if (isPast) return;

        if (!selectedStart) {
            setSelectedStart(date);
            setSelectedEnd(null);
        } else if (!selectedEnd) {
            const start = selectedStart < date ? selectedStart : date;
            const end = selectedStart < date ? date : selectedStart;

            setSelectedStart(null);
            setSelectedEnd(null);
            setHoveredDate(null);

            onSelectSlot({ start, end });
        } else {
            setSelectedStart(date);
            setSelectedEnd(null);
            setHoveredDate(null);
        }
    };

    const handleDateHover = (date: Date) => {
        if (!onSelectSlot) return;
        if (selectedStart && !selectedEnd) {
            setHoveredDate(date);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!onSelectSlot || !selectedStart || selectedEnd) return;

        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);

        if (element && element.classList.contains('calendar-day')) {
            const dateStr = element.getAttribute('data-date');
            if (dateStr) {
                const date = new Date(dateStr);
                setHoveredDate(date);
            }
        }
    };

    const isDateInRange = (date: Date): boolean => {
        if (!selectedStart) return false;

        if (!selectedEnd && hoveredDate) {
            const start = selectedStart < hoveredDate ? selectedStart : hoveredDate;
            const end = selectedStart < hoveredDate ? hoveredDate : selectedStart;
            return moment(date).isBetween(start, end, 'day', '[]');
        }

        if (!selectedEnd) {
            return moment(date).isSame(selectedStart, 'day');
        }

        const start = selectedStart < selectedEnd ? selectedStart : selectedEnd;
        const end = selectedStart < selectedEnd ? selectedEnd : selectedStart;

        return moment(date).isBetween(start, end, 'day', '[]');
    };

    const isCheckInDate = (date: Date): boolean => {
        if (!selectedStart) return false;
        return moment(date).isSame(selectedStart, 'day');
    };

    const isCheckOutDate = (date: Date): boolean => {
        if (!hoveredDate && !selectedEnd) return false;
        const compareDate = selectedEnd || hoveredDate;
        return compareDate ? moment(date).isSame(compareDate, 'day') : false;
    };

    const getEventsForDay = (date: Date) => {
        const dayMoment = moment(date).startOf('day');
        return reservations.filter((reservation) => {
            const checkIn = moment(reservation.check_in_date).startOf('day');
            const checkOut = moment(reservation.check_out_date).startOf('day');
            return dayMoment.isSameOrAfter(checkIn) && dayMoment.isSameOrBefore(checkOut);
        });
    };

    const getReservationsForWeek = (week: Date[]) => {
        const weekStart = week[0];
        const weekEnd = week[6];

        return reservations.filter((reservation) => {
            const checkIn = moment(reservation.check_in_date).startOf('day');
            const checkOut = moment(reservation.check_out_date).startOf('day');
            const wStart = moment(weekStart).startOf('day');
            const wEnd = moment(weekEnd).startOf('day');

            return checkIn.isSameOrBefore(wEnd) && checkOut.isSameOrAfter(wStart);
        });
    };

    const getReservationPosition = (reservation: Reservation, week: Date[]) => {
        const checkIn = moment(reservation.check_in_date).startOf('day');
        const checkOut = moment(reservation.check_out_date).startOf('day');
        const weekStart = moment(week[0]).startOf('day');
        const weekEnd = moment(week[6]).startOf('day');

        // Check if reservation starts before or continues beyond the visible week
        const startsBeforeWeek = checkIn.isBefore(weekStart);
        const endsAfterWeek = checkOut.isAfter(weekEnd);

        // Find start position
        let start = 0;
        for (let i = 0; i < 7; i++) {
            if (moment(week[i]).isSameOrAfter(checkIn)) {
                // If reservation starts before visible week, start at 0% (full block from day 1)
                // If reservation starts on this day, start at 50% (middle of check-in day)
                start = startsBeforeWeek ? i : i + 0.5;
                break;
            }
        }

        // Calculate span with 50% check-in and 50% check-out
        // This allows same-day turnover bookings
        const displayStart = moment.max(checkIn, weekStart);
        const displayEnd = moment.min(checkOut, weekEnd);
        let span = displayEnd.diff(displayStart, 'days');

        // Always extend span by 0.5 to reach noon of the checkout day
        // This ensures the reservation bar covers from noon of check-in to noon of check-out
        span += 0.5;

        return { start, span: Math.max(0.5, span) };
    };

    const assignReservationsToRows = (reservations: Reservation[], week: Date[]) => {
        const reservationsWithRows: Array<{ reservation: Reservation; row: number }> = [];

        const sorted = [...reservations].sort((a, b) => {
            const aStart = moment(a.check_in_date).valueOf();
            const bStart = moment(b.check_in_date).valueOf();
            if (aStart !== bStart) return aStart - bStart;

            const aDuration = moment(a.check_out_date).diff(moment(a.check_in_date));
            const bDuration = moment(b.check_out_date).diff(moment(b.check_in_date));
            return bDuration - aDuration;
        });

        sorted.forEach(reservation => {
            const { start, span } = getReservationPosition(reservation, week);
            const end = start + span;

            let row = 0;
            while (true) {
                const conflict = reservationsWithRows.find(r => {
                    if (r.row !== row) return false;
                    const otherPos = getReservationPosition(r.reservation, week);
                    const otherEnd = otherPos.start + otherPos.span;
                    // Check overlap - allow same-day turnover (check-out meets check-in at 50%)
                    return !(end <= otherPos.start || start >= otherEnd);
                });

                if (!conflict) {
                    reservationsWithRows.push({ reservation, row });
                    break;
                }
                row++;
            }
        });

        return reservationsWithRows;
    };

    const renderMonth = () => {
        const { year, month } = currentMonth;
        const firstDay = moment().year(year).month(month).startOf('month');
        const lastDay = moment().year(year).month(month).endOf('month');
        const startDate = moment(firstDay).startOf('week');
        const endDate = moment(lastDay).endOf('week');

        const weeks: Date[][] = [];
        let currentWeek: Date[] = [];
        let currentDate = moment(startDate);

        while (currentDate.isSameOrBefore(endDate)) {
            currentWeek.push(currentDate.toDate());

            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }

            currentDate.add(1, 'day');
        }

        const showSelectionInfo = !isMobile && selectedStart && !selectedEnd;

        return (
            <div className="multi-month-calendar-month">
                <div className="month-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handlePrevMonth}
                            className="btn btn-outline-primary btn-sm"
                        >
                            <ChevronLeft size={20} />
                        </Button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h5 style={{ margin: 0 }}>{firstDay.format('MMMM YYYY')}</h5>
                            <button
                                onClick={handleToday}
                                style={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    background: '#f0f0f0',
                                    padding: '5px 12px',
                                    cursor: 'pointer',
                                    fontWeight: 'normal',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#e0e0e0')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#f0f0f0')}
                            >
                                Today
                            </button>
                        </div>

                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleNextMonth}
                            className="btn btn-outline-primary btn-sm"
                        >
                            <ChevronRight size={20} />
                        </Button>
                    </div>
                </div>

                {showSelectionInfo && (
                    <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                        <div>
                            <strong>Check-in:</strong> {moment(selectedStart).format('MMM DD, YYYY')}
                            <span className="ms-3 text-muted">← Now click check-out date</span>
                        </div>
                        <Button
                            variant="link"
                            className="text-secondary p-0 ms-3"
                            onClick={handleClearSelection}
                            style={{ minWidth: 'auto' }}
                            title="Clear selection (or press ESC)"
                        >
                            <X size={20} />
                        </Button>
                    </div>
                )}

                <div className="weekdays-header">
                    {WEEKDAYS.map((day) => (
                        <div key={day} className="weekday">
                            {day}
                        </div>
                    ))}
                </div>

                <div className="weeks-container">
                    {weeks.map((week, weekIndex) => {
                        const weekReservations = getReservationsForWeek(week);
                        const reservationsWithRows = assignReservationsToRows(weekReservations, week);

                        const maxRow = Math.max(0, ...reservationsWithRows.map(r => r.row));
                        const visibleRows = Math.min(maxRow + 1, MAX_VISIBLE_EVENTS);

                        // Dynamic sizing based on reservation count
                        const reservationHeight = 22;
                        const reservationGap = 0.5;

                        // For weeks with 3 or fewer reservations: compact layout
                        // For weeks with more than 3 reservations: larger cells for better click accuracy
                        const isCrowded = maxRow >= 3; // 3+ visible rows means 4+ reservations
                        const baseHeight = isCrowded ? 120 : 100;
                        const firstReservationTop = isCrowded ? 36 : 28;
                        const dynamicMinHeight = Math.max(baseHeight, firstReservationTop + (visibleRows * (reservationHeight + reservationGap)) + 10);

                        // Count total hidden reservations for the entire week
                        const totalHiddenReservations = reservationsWithRows.filter(({ row }) => row >= MAX_VISIBLE_EVENTS).length;
                        const hasMoreInWeek = totalHiddenReservations > 0;

                        // Calculate position for "+X more" below last visible reservation
                        const lastVisibleRow = Math.min(maxRow, MAX_VISIBLE_EVENTS - 1);
                        const moreIndicatorTop = firstReservationTop + ((lastVisibleRow + 1) * (reservationHeight + reservationGap)) + 0.5;

                        return (
                            <div
                                key={weekIndex}
                                className="week-wrapper"
                                style={{ minHeight: `${dynamicMinHeight}px` }}
                            >
                                <div className="week-row">
                                    {week.map((date) => {
                                        const isCurrentMonth = moment(date).month() === month;
                                        const isToday = moment(date).isSame(moment(), 'day');
                                        const isPast = moment(date).isBefore(moment(), 'day');
                                        const isInSelection = isDateInRange(date);
                                        const isCheckIn = isCheckInDate(date);
                                        const isCheckOut = isCheckOutDate(date);

                                        if (!isCurrentMonth) {
                                            return (
                                                <div
                                                    key={date.toISOString()}
                                                    className="calendar-day empty"
                                                >
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={date.toISOString()}
                                                className={`
                                                    calendar-day
                                                    ${isToday ? 'today' : ''}
                                                    ${isPast ? 'past' : ''}
                                                    ${isInSelection ? 'in-selection' : ''}
                                                    ${isCheckIn ? 'check-in-date' : ''}
                                                    ${isCheckOut ? 'check-out-date' : ''}
                                                    ${!onSelectSlot ? 'disabled-selection' : ''}
                                                    ${isCrowded ? 'crowded-week' : ''}
                                                `}
                                                data-date={date.toISOString()}
                                                onClick={(e) => {
                                                    if ((e.target as HTMLElement).closest('.show-more-link, .show-more-indicator')) {
                                                        return;
                                                    }
                                                    handleDateClick(date);
                                                }}
                                                onMouseEnter={() => handleDateHover(date)}
                                                onTouchStart={() => handleDateHover(date)}
                                                style={{
                                                    cursor: !onSelectSlot ? 'default' : isPast ? 'not-allowed' : 'pointer',
                                                    minHeight: `${dynamicMinHeight}px`,
                                                    padding: isCrowded ? '10px' : '6px'
                                                }}
                                            >
                                                <div className="day-number">
                                                    {moment(date).date()}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Show +X more indicator for the entire week */}
                                {hasMoreInWeek && !expandedWeeks.has(weekIndex) && (
                                    <div
                                        className="show-more-link week-show-more"
                                        style={{
                                            top: `${moreIndicatorTop}px`,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedWeeks(prev => {
                                                const newSet = new Set(prev);
                                                newSet.add(weekIndex);
                                                return newSet;
                                            });
                                        }}
                                    >
                                        +{totalHiddenReservations} more
                                    </div>
                                )}

                                <div className="week-reservations">
                                    {reservationsWithRows
                                        .filter(({ row }) => row < MAX_VISIBLE_EVENTS)
                                        .map(({ reservation, row }) => {
                                            const { start, span } = getReservationPosition(reservation, week);
                                            const colors = getStatusColor(reservation.reservation_state);
                                            const house = houses.find(h => h.public_id === reservation.house_id);

                                            // Admin/Super Admin: use house color, Normal users: use status colors
                                            const backgroundColor = isAdminOrSuperAdmin
                                                ? (house?.color || colors.bg)
                                                : colors.bg;
                                            const borderColor = isAdminOrSuperAdmin
                                                ? (house?.color || colors.border)
                                                : colors.border;
                                            const textColor = isAdminOrSuperAdmin ? '#ffffff' : colors.text;

                                            // Bold for confirmed, normal for pending
                                            const fontWeight = isAdminOrSuperAdmin
                                                ? (reservation.reservation_state === 'confirmed' ? '700' : '400')
                                                : '400';

                                            const topPosition = firstReservationTop + (row * (reservationHeight + reservationGap));

                                            // Mobile uses 2px gaps, desktop uses 4px gaps
                                            const gapWidth = isMobile ? 2 : 4;
                                            const totalGapWidth = 6 * gapWidth; // 6 gaps between 7 columns
                                            const columnWidth = `calc((100% - ${totalGapWidth}px) / 7)`;
                                            const leftPosition = `calc(${columnWidth} * ${start} + ${gapWidth * start}px)`;
                                            const widthCalc = `calc(${columnWidth} * ${span} + ${gapWidth * (span - 1)}px)`;

                                            const isHovered = hoveredReservationId === reservation.public_id;

                                            return (
                                                <div
                                                    key={reservation.public_id}
                                                    className={`reservation-bar ${isHovered ? 'reservation-bar-hovered' : ''}`}
                                                    style={{
                                                        backgroundColor: backgroundColor,
                                                        borderLeft: `4px solid ${borderColor}`,
                                                        color: textColor,
                                                        top: `${topPosition}px`,
                                                        left: leftPosition,
                                                        width: widthCalc,
                                                        fontWeight: fontWeight,
                                                    }}
                                                    onMouseEnter={() => setHoveredReservationId(reservation.public_id)}
                                                    onMouseLeave={() => setHoveredReservationId(null)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onSelectReservation(reservation);
                                                    }}
                                                    title={`${reservation.name_first_guess} - ${house?.name || house?.address || 'House'}`}
                                                >
                                                    <span className="reservation-bar-content">
                                                        {reservation.name_first_guess || 'Guest'} - {house?.name || house?.address || 'House'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </div>

                                {/* Expanded section showing hidden reservations below */}
                                {expandedWeeks.has(weekIndex) && maxRow >= MAX_VISIBLE_EVENTS && (
                                    <div className="expanded-reservations">
                                        <div className="expanded-reservations-header">
                                            <span>Additional Reservations</span>
                                            <button
                                                className="expanded-close-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedWeeks(prev => {
                                                        const newSet = new Set(prev);
                                                        newSet.delete(weekIndex);
                                                        return newSet;
                                                    });
                                                }}
                                                title="Close"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                        <div className="expanded-reservations-list">
                                            {reservationsWithRows
                                                .filter(({ row }) => row >= MAX_VISIBLE_EVENTS)
                                                .map(({ reservation }) => {
                                                    const colors = getStatusColor(reservation.reservation_state);
                                                    const house = houses.find(h => h.public_id === reservation.house_id);
                                                    const checkIn = moment(reservation.check_in_date).format('MMM DD');
                                                    const checkOut = moment(reservation.check_out_date).format('MMM DD');

                                                    // Admin/Super Admin: use house color, Normal users: use status colors
                                                    const backgroundColor = isAdminOrSuperAdmin
                                                        ? (house?.color || colors.bg)
                                                        : colors.bg;
                                                    const borderColor = isAdminOrSuperAdmin
                                                        ? (house?.color || colors.border)
                                                        : colors.border;
                                                    const textColor = isAdminOrSuperAdmin ? '#ffffff' : colors.text;

                                                    // Bold for confirmed, normal for pending
                                                    const fontWeight = isAdminOrSuperAdmin
                                                        ? (reservation.reservation_state === 'confirmed' ? '700' : '400')
                                                        : '400';

                                                    const isHovered = hoveredReservationId === reservation.public_id;

                                                    return (
                                                        <div
                                                            key={reservation.public_id}
                                                            className={`expanded-reservation-item ${isHovered ? 'reservation-bar-hovered' : ''}`}
                                                            style={{
                                                                backgroundColor: backgroundColor,
                                                                borderLeft: `4px solid ${borderColor}`,
                                                                color: textColor,
                                                                fontWeight: fontWeight,
                                                            }}
                                                            onMouseEnter={() => setHoveredReservationId(reservation.public_id)}
                                                            onMouseLeave={() => setHoveredReservationId(null)}
                                                            onClick={() => onSelectReservation(reservation)}
                                                        >
                                                            <div className="expanded-reservation-name">
                                                                {reservation.name_first_guess || 'Guest'}
                                                            </div>
                                                            <div className="expanded-reservation-details">
                                                                <span>{house?.name || house?.address || 'House'}</span>
                                                                <span className="expanded-reservation-dates">
                                                                    {checkIn} → {checkOut}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="calendar-view-container" style={{ height: '100%' }}>
            {isMobile && selectedStart && !selectedEnd && (
                <div
                    className="alert alert-info sticky-top d-flex justify-content-between align-items-start"
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        margin: 0,
                        borderRadius: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    <div>
                        <strong>Check-in:</strong> {moment(selectedStart).format('MMM DD, YYYY')}
                        <div className="text-muted small mt-1">👆 Tap check-out date</div>
                    </div>
                    <Button
                        variant="link"
                        className="text-secondary p-0 ms-2"
                        onClick={handleClearSelection}
                        style={{ minWidth: 'auto' }}
                        title="Clear selection"
                    >
                        <X size={20} />
                    </Button>
                </div>
            )}

            <div
                className="multi-month-calendar-container"
                onTouchMove={handleTouchMove}
            >
                {renderMonth()}
            </div>
        </div>
    );
};

export default CalendarView;
